import { useRef, useState } from 'react';
import { CITATION_MARKER } from '@lenderhound/shared';
import { askFollowUp, ApiError, type Reference } from '../api.ts';

/**
 * Follow-up chat.
 *
 * The suggested questions are not decoration. A broker handed an empty box after a long
 * answer tends to ask nothing; the three prompts are the ones the answer most often
 * leaves open - a cheaper loan amount, a different delivery channel, a different program.
 */

const SUGGESTIONS = [
  'What if we drop the loan amount?',
  'Which delivery channel is safest here?',
  'What would you need from the borrower to make this work?',
];

interface Turn {
  role: 'user' | 'assistant';
  text: string;
  tools?: string[];
}

interface Props {
  inquiryId: string;
  /** Shared with the results view so a chip opens the same source panel. */
  onReferences: (refs: Reference[]) => void;
  onCite: (id: number) => void;
}

function Prose({ text, onCite }: { text: string; onCite: (id: number) => void }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  CITATION_MARKER.lastIndex = 0;
  for (let m = CITATION_MARKER.exec(text); m; m = CITATION_MARKER.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const id = Number(m[1]);
    parts.push(
      <button key={`${m.index}-${id}`} className="chip" type="button" onClick={() => onCite(id)}>
        &#9656; {id}
      </button>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export default function Chat({ inquiryId, onReferences, onCite }: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  async function send(question: string): Promise<void> {
    if (!question.trim() || busy) return;
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    setTurns((t) => [...t, { role: 'user', text: question }]);
    setDraft('');
    setBusy(true);
    setError('');
    try {
      const reply = await askFollowUp(inquiryId, question, ctl.signal);
      // Merge rather than replace: a chip from the opening answer must keep working
      // after a follow-up adds its own references.
      onReferences(reply.references);
      setTurns((t) => [...t, { role: 'assistant', text: reply.text, tools: reply.toolCalls }]);
    } catch (e) {
      if (!ctl.signal.aborted) {
        // A sentence the server wrote wins over the generic one, the way App.tsx's
        // friendlyError treats it: the daily follow-up ceiling is the case that needs it,
        // because only the server knows how many questions are left and when they come back.
        setError(e instanceof ApiError && e.detail
          ? e.detail
          : 'That question could not be answered. Try rephrasing it.');
        // Drop the unanswered question rather than leaving it hanging above an error,
        // which reads as though it was answered and the answer was lost.
        setTurns((t) => t.slice(0, -1));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 20 }}>Ask a follow-up</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--muted)', fontSize: 13 }}>
        Every answer is checked against the same documents, with the same citations.
      </p>

      {turns.map((t, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          {t.role === 'user' ? (
            <div style={{
              background: 'var(--cream)', borderRadius: 8, padding: '8px 12px',
              fontSize: 14, fontWeight: 500,
            }}>{t.text}</div>
          ) : (
            <div style={{ fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              <Prose text={t.text} onCite={onCite} />
              {t.tools && t.tools.length > 0 ? (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--faint)' }}>
                  checked: {[...new Set(t.tools)].join(', ').replace(/_/g, ' ')}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ))}

      {busy ? (
        <div className="stage"><span className="spin" /> Checking the documents…</div>
      ) : null}
      {error ? <div className="err-text">{error}</div> : null}

      {turns.length === 0 && !busy ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" className="btn btn-ghost"
                    style={{ fontSize: 13, padding: '6px 12px' }}
                    onClick={() => { void send(s); }}>{s}</button>
          ))}
        </div>
      ) : null}

      <form
        onSubmit={(e) => { e.preventDefault(); void send(draft); }}
        style={{ display: 'flex', gap: 8, marginTop: 8 }}
      >
        <input
          className="finput" value={draft} disabled={busy}
          placeholder="What if the FICO were 700? Would a 2-unit change this?"
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>
          Ask
        </button>
      </form>
    </div>
  );
}
